// Backoff between transport retries. Two short waits keep the worst case
// around a second, so a retried request still resolves inside the window a
// user reads as "loading" rather than "broken".
const RETRY_BACKOFF_MS = [250, 750];

/**
 * The URL a request names, however the caller spelled it.
 *
 * A `Request` stringifies to `[object Request]`, so anything reporting on a
 * failure has to ask for the URL rather than interpolate the argument.
 */
export function toRequestUrl(input: RequestInfo): string {
  return typeof input === "string" ? input : input.url;
}

/**
 * The outcome of reading a response as JSON.
 *
 * A payload that will not parse is not the same as an absent one: the request
 * reached a server and the status it answered with is still meaningful, so the
 * two cases stay distinguishable rather than collapsing into a thrown parse
 * error that carries neither.
 */
type JsonResult =
  | { parsed: true; status: number; body: unknown }
  | { parsed: false; status: number };

/**
 * Reads a JSON response, with bounded retries for a request that fails in
 * transit — whether the connection drops or the payload arrives unusable.
 *
 * A connection that drops, or a keep-alive socket the server closes just as
 * the client reuses it, rejects the `fetch` promise outright. Chromium and
 * Gecko hide most of that by replaying an idempotent request on a fresh
 * connection; WebKit does not, and surfaces a bare `TypeError` instead. That
 * asymmetry turns an invisible blip on one engine into a hard failure on
 * another, so replaying the request here keeps the difference out of the UI.
 *
 * A body truncated part-way through is the same class of event wearing a
 * healthy HTTP response: nothing before the parse reveals it, and it is just as
 * transient, so it is replayed on the same terms. A proxy or CDN error page is
 * not — it says so in its content type, and is handed straight back.
 *
 * An empty body is not junk where one was never promised. A bodiless 401, a
 * 204, a 304 — all read as `{}` so the caller can act on the status rather than
 * being replayed twice over something that was never going to parse. A 2xx that
 * owed a payload and sent nothing is a different event, and stays damaged.
 */
export async function fetchJsonWithRetry(
  input: RequestInfo,
  init?: RequestInit
): Promise<JsonResult> {
  // Replaying a request that mutates state could double-apply it, so only
  // idempotent methods get a second chance.
  const retries = isIdempotent(input, init) ? RETRY_BACKOFF_MS.length : 0;

  for (let index = 0; index < retries; index++) {
    const result = await attemptJson(input, init);
    if (result) {
      return result;
    }
    await delay(RETRY_BACKOFF_MS[index]);
  }

  // The final attempt is unguarded so a genuine failure reaches the caller
  // with its original error.
  return readJson(await fetch(input, init));
}

/** Resolves `undefined` when the attempt failed in a way worth replaying. */
async function attemptJson(
  input: RequestInfo,
  init?: RequestInit
): Promise<JsonResult | undefined> {
  let response: Response;
  try {
    response = await fetch(input, init);
  } catch (error) {
    // Only a transport failure is worth replaying. A deliberate abort raises a
    // DOMException and must surface immediately rather than be retried against
    // the caller's intent.
    if (error instanceof TypeError) {
      return undefined;
    }
    throw error;
  }

  const result = await readJson(response);
  if (result.parsed) {
    return result;
  }

  // A payload that declares itself as something other than JSON is
  // infrastructure answering in the API's place — a proxy or CDN error page.
  // That is a deliberate response rather than a damaged one: replaying it adds
  // load to something already struggling and will not change the answer, so it
  // is handed back on the first attempt with its status intact.
  return declaresJson(response) ? undefined : result;
}

function declaresJson(response: Response): boolean {
  // The subtype is case-insensitive, so a server spelling it `Application/JSON`
  // must not read as a page served in the API's place.
  const contentType = response.headers.get("content-type")?.toLowerCase();
  // Nothing declared is treated as a damaged payload rather than a deliberate
  // one, so it keeps its retry.
  return !contentType || contentType.includes("json");
}

async function readJson(response: Response): Promise<JsonResult> {
  const { status } = response;

  let text: string;
  try {
    text = await response.text();
  } catch (error) {
    // The body stream failed part-way through, so there is nothing to parse.
    // Reading is the only thing that surfaces this — the response itself
    // arrived looking healthy.
    if (!(error instanceof TypeError)) {
      // An abort lands here just as it lands on `fetch` itself once the body is
      // already streaming, and must surface rather than be swallowed into a
      // replay the caller has just cancelled.
      throw error;
    }
    return { parsed: false, status };
  }

  if (text === "") {
    // A status that owes no body has answered; one that owed a payload has not.
    return allowsEmptyBody(status)
      ? { parsed: true, status, body: {} }
      : { parsed: false, status };
  }

  try {
    return { parsed: true, status, body: JSON.parse(text) };
  } catch {
    return { parsed: false, status };
  }
}

function allowsEmptyBody(status: number): boolean {
  // No body by definition, or a refusal that need not explain itself.
  return status === 204 || status === 205 || status === 304 || status >= 400;
}

function isIdempotent(input: RequestInfo, init?: RequestInit): boolean {
  // A caller can name the method on either argument, and a `Request` carrying a
  // body would also fail a replay outright once its stream is consumed. Reading
  // both — `init` first, matching how `fetch` itself resolves the conflict —
  // keeps a mutating request from slipping past this guard unnoticed.
  const requested =
    init?.method ?? (typeof input === "string" ? undefined : input.method);
  const method = (requested ?? "GET").toUpperCase();
  return method === "GET" || method === "HEAD";
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
